// ============================================================
//  ALTIMO Medical Center — Dashboard.jsx  (v2 — KPIs + Alertas + Gráfica)
//  Ubicación: src/components/Dashboard.jsx
// ============================================================

import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import api from '../apiClient'; // ← NUEVO: reemplaza window.altimo (IPC) por HTTP al servidor

// ── Paleta y tokens ─────────────────────────────────────────
const COLOR = {
  azul:     '#1B2A5E',
  azulClaro:'#e8f0fb',
  verde:    '#1D9E75',
  verdeClaro:'#d4edda',
  rojo:     '#dc3545',
  rojoClaro:'#f8d7da',
  amarillo: '#ffc107',
  amarilloClaro:'#fff3cd',
  gris:     '#6c757d',
  grisClaro:'#f8fafd',
  borde:    '#dde3ea',
};

// ── Estilos ──────────────────────────────────────────────────
const S = {
  pagina: {
    background: '#f0f4f8', minHeight: '100vh',
    padding: '16px 20px', fontFamily: 'Segoe UI, sans-serif',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '18px',
  },
  headerTitulo: { fontSize: '20px', fontWeight: '800', color: COLOR.azul },
  headerSub: { fontSize: '12px', color: COLOR.gris, marginTop: '2px' },
  refreshDot: (activo) => ({
    width: '8px', height: '8px', borderRadius: '50%',
    background: activo ? COLOR.verde : '#adb5bd',
    display: 'inline-block', marginRight: '6px',
    animation: activo ? 'pulse 1.5s infinite' : 'none',
  }),
  kpiGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px', marginBottom: '16px',
  },
  kpiCard: (color, activo) => ({
    background: '#fff', borderRadius: '12px',
    border: `2px solid ${activo ? color : COLOR.borde}`,
    padding: '16px 18px', cursor: 'pointer',
    transition: 'all .15s', position: 'relative', overflow: 'hidden',
    boxShadow: activo ? `0 4px 16px ${color}33` : 'none',
  }),
  kpiBarra: (color) => ({
    position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: color,
  }),
  kpiLabel: {
    fontSize: '10px', color: COLOR.gris, fontWeight: '700',
    textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '8px',
  },
  kpiValor: (color) => ({
    fontSize: '28px', fontWeight: '800', color, lineHeight: '1',
  }),
  kpiSub: { fontSize: '11px', color: COLOR.gris, marginTop: '6px' },
  kpiIcono: (color) => ({
    position: 'absolute', right: '14px', top: '14px',
    fontSize: '24px', opacity: 0.15,
  }),
  kpiTendencia: (sube) => ({
    display: 'inline-flex', alignItems: 'center', gap: '3px',
    fontSize: '11px', fontWeight: '700',
    color: sube ? COLOR.verde : COLOR.rojo,
    background: sube ? COLOR.verdeClaro : COLOR.rojoClaro,
    padding: '2px 7px', borderRadius: '99px', marginTop: '6px',
  }),
  fila2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' },
  fila3: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px', marginBottom: '14px' },
  card: {
    background: '#fff', borderRadius: '12px',
    border: `1px solid ${COLOR.borde}`, padding: '18px',
    marginBottom: '14px',
  },
  cardTitulo: {
    fontSize: '12px', fontWeight: '700', color: COLOR.azul,
    textTransform: 'uppercase', letterSpacing: '.05em',
    marginBottom: '14px', paddingBottom: '10px',
    borderBottom: `2px solid ${COLOR.azulClaro}`,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: {
    textAlign: 'left', padding: '8px 12px', fontSize: '10px', color: COLOR.gris,
    fontWeight: '700', borderBottom: `2px solid ${COLOR.azulClaro}`,
    background: COLOR.grisClaro, textTransform: 'uppercase', letterSpacing: '.04em',
  },
  td: { padding: '10px 12px', borderBottom: `1px solid #eef1f6`, color: '#333', fontSize: '13px' },
  pill: (bg, color) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: '99px',
    fontSize: '10px', fontWeight: '700', background: bg, color,
  }),
  btnPrimario: {
    background: COLOR.verde, color: '#fff', border: 'none',
    borderRadius: '7px', padding: '7px 16px', fontSize: '12px',
    fontWeight: '600', cursor: 'pointer',
  },
  btnSecundario: {
    background: '#fff', color: '#495057', border: `1px solid ${COLOR.borde}`,
    borderRadius: '7px', padding: '6px 12px', fontSize: '11px', cursor: 'pointer',
  },
  // Notificaciones
  notifCard: (leida) => ({
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    padding: '10px 12px', borderRadius: '8px', marginBottom: '6px',
    background: leida ? '#f8fafd' : '#fff',
    border: `1px solid ${leida ? COLOR.borde : COLOR.amarillo}`,
    cursor: 'pointer', transition: 'all .15s',
    opacity: leida ? 0.6 : 1,
  }),
  notifPunto: (leida) => ({
    width: '8px', height: '8px', borderRadius: '50%', marginTop: '5px', flexShrink: 0,
    background: leida ? '#adb5bd' : COLOR.verde,
  }),
  notifTitulo: (leida) => ({
    fontSize: '12px', fontWeight: leida ? '400' : '700',
    color: leida ? COLOR.gris : COLOR.azul,
  }),
  notifFecha: { fontSize: '10px', color: '#adb5bd', marginTop: '2px' },
  // Barra mini chart
  barraChart: { display: 'flex', alignItems: 'flex-end', gap: '4px', height: '60px', padding: '4px 0' },
  barraDia: (alto, activa) => ({
    flex: 1, background: activa ? COLOR.verde : COLOR.azulClaro,
    borderRadius: '4px 4px 0 0', height: `${alto}%`,
    minHeight: '4px', transition: 'background .2s',
    cursor: 'pointer', position: 'relative',
  }),
  // Row expandible
  rowDetalle: {
    background: '#f8fafd', padding: '12px 16px',
    borderBottom: `1px solid ${COLOR.azulClaro}`,
    fontSize: '12px', color: '#495057',
  },
};

// ── Componentes auxiliares ───────────────────────────────────
const PILL_ESTADOS = {
  Activa:          { bg: '#d4edda', color: '#155724' },
  Alta:            { bg: '#fff3cd', color: '#856404' },
  Ambulatorio:     { bg: '#cce5ff', color: '#004085' },
};

function PillEstado({ texto }) {
  const e = PILL_ESTADOS[texto] || { bg: '#e2e3e5', color: '#383d41' };
  return <span style={S.pill(e.bg, e.color)}>{texto}</span>;
}

function KpiCard({ label, valor, sub, color, icono, tendencia, onClick, activo }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      style={{ ...S.kpiCard(color, activo || hover), ...(hover ? { transform:'translateY(-2px)' } : {}) }}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}>
      <div style={S.kpiBarra(color)} />
      <div style={S.kpiLabel}>{label}</div>
      <div style={S.kpiValor(color)}>{valor ?? '—'}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
      {tendencia !== undefined && (
        <div style={S.kpiTendencia(tendencia >= 0)}>
          {tendencia >= 0 ? '▲' : '▼'} {Math.abs(tendencia)}%
        </div>
      )}
      <div style={S.kpiIcono(color)}>{icono}</div>
    </div>
  );
}

// Gráfica de barras 7 días (inline)
function MiniBarChart({ datos, onDiaClick, diaActivo }) {
  const max = Math.max(...datos.map(d => d.total || 0), 1);
  return (
    <div>
      <div style={S.barraChart}>
        {datos.map((d, i) => {
          const pct = Math.max((d.total / max) * 100, 4);
          const activa = diaActivo === d.fecha;
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <div
                title={`${d.fecha}: $${Number(d.total||0).toFixed(2)}`}
                style={S.barraDia(pct, activa)}
                onClick={() => onDiaClick && onDiaClick(d)}
              />
              <div style={{ fontSize: '9px', color: activa ? COLOR.azul : '#adb5bd', fontWeight: activa ? '700' : '400', whiteSpace: 'nowrap' }}>
                {d.label}
              </div>
            </div>
          );
        })}
      </div>
      {diaActivo && (() => {
        const d = datos.find(x => x.fecha === diaActivo);
        return d ? (
          <div style={{ fontSize: '12px', color: COLOR.azul, fontWeight: '600', marginTop: '4px', textAlign: 'center' }}>
            {d.fecha}: ${Number(d.total||0).toFixed(2)} · {d.facturas||0} factura(s)
          </div>
        ) : null;
      })()}
    </div>
  );
}

// ── Helper: generar últimos 7 días ──────────────────────────
function ultimos7Dias() {
  const dias = [];
  const DIAS = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    dias.push({
      fecha: d.toISOString().slice(0, 10),
      label: i === 0 ? 'Hoy' : DIAS[d.getDay()],
      total: 0, facturas: 0,
    });
  }
  return dias;
}

// ── Detectar área por rol ────────────────────────────────────
function areaDeRol(rol) {
  if (!rol) return 'Todas';
  const r = rol.toLowerCase();
  if (r.includes('lab')) return 'Laboratorio';
  if (r.includes('factur') || r.includes('caja')) return 'Facturacion';
  if (r.includes('farm')) return 'Farmacia';
  if (r.includes('jefa_enfer')) return 'JefaEnfermeria';
  if (r.includes('medic')) return 'Medicos';
  if (r.includes('admin')) return 'Administracion';
  return 'Todas';
}

// ════════════════════════════════════════════════════════════
//  COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════
export default function Dashboard({ usuario, onVerCuenta, onIrAModulo, onIrAExpedienteLab }) {
  const [kpis,        setKpis]        = useState(null);
  const [pacientes,   setPacientes]   = useState([]);
  const [alertas,     setAlertas]     = useState(null);
  const [notifs,      setNotifs]      = useState([]);
  const [semana,      setSemana]      = useState(ultimos7Dias());
  const [cargando,    setCargando]    = useState(true);
  const [error,       setError]       = useState(null);
  const [refreshing,  setRefreshing]  = useState(false);
  const [ultimaAct,   setUltimaAct]   = useState(null);

  // UI state
  const [kpiActivo,   setKpiActivo]   = useState(null);
  const [alertasOpen, setAlertasOpen] = useState(true);
  const [notifsOpen,  setNotifsOpen]  = useState(true);
  const [rowAbierta,  setRowAbierta]  = useState(null);
  const [diaActivo,   setDiaActivo]   = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('Todos');

  // ── Panel médico ("Mi panel") — solo rol medico / jefe_medico ──
  const [misPacientes,    setMisPacientes]    = useState([]);
  const [misLabs,         setMisLabs]         = useState([]);
  const [misPrescrip,     setMisPrescrip]     = useState([]);
  const [cargandoMedico,  setCargandoMedico]  = useState(false);
  const [miPanelOpen,     setMiPanelOpen]     = useState(true);
  const [enfPanelOpen,    setEnfPanelOpen]    = useState(true);
  const [labPanelOpen,    setLabPanelOpen]    = useState(true);
  const [farmPanelOpen,   setFarmPanelOpen]   = useState(true);

  // ── Panel enfermería — solo rol enfermeria / jefa_enfermeria / supervisora_turno ──
  const [indicEnf,           setIndicEnf]           = useState([]);
  const [cargandoIndicEnf,   setCargandoIndicEnf]   = useState(false);

  // ── Panel laboratorio — solo rol laboratorio ──
  const [ordenesLabPend,     setOrdenesLabPend]     = useState([]);
  const [resultadosHoyLab,   setResultadosHoyLab]   = useState(0);
  const [cargandoLab,        setCargandoLab]        = useState(false);

  // ── Panel farmacia — solo rol farmacia ──
  const [inventarioFarm,       setInventarioFarm]       = useState([]);
  const [prescripPendFarm,     setPrescripPendFarm]     = useState([]);
  const [devolucPendFarm,      setDevolucPendFarm]      = useState([]);
  const [cargandoFarm,         setCargandoFarm]         = useState(false);

  const tablaRef = useRef();
  const area = areaDeRol(usuario?.rol);
  const esRolMedico = usuario?.rol === 'medico' || usuario?.rol === 'jefe_medico';
  const esRolEnfermeria = ['enfermeria', 'jefa_enfermeria', 'supervisora_turno'].includes(usuario?.rol);
  const esRolLaboratorio = usuario?.rol === 'laboratorio';
  const esRolFarmacia = usuario?.rol === 'farmacia';
  // Montos/facturación: información financiera del paciente, no debe ser
  // visible para roles clínicos/operativos sin función de cobro o caja.
  const verMontos = !esRolEnfermeria && !esRolLaboratorio && !esRolFarmacia;

  // ── Cargar datos ─────────────────────────────────────────
  const cargarDatos = useCallback(async (silencioso = false) => {
    try {
      if (!silencioso) setCargando(true);
      else             setRefreshing(true);

      const [rKpis, rPac, rAlertas, rNotifs] = await Promise.all([
        api.dashboard.kpis(),
        api.dashboard.pacientesActivos(),
        api.reportes.alertas(),
        api.notificaciones.listar({ area, rol: usuario?.rol, solo_no_leidas: false, limite: 50 }),
      ]);

      if (!rKpis.ok)    throw new Error(rKpis.error);
      if (!rPac.ok)     throw new Error(rPac.error);
      if (!rAlertas.ok) throw new Error(rAlertas.error);

      setKpis(rKpis.data);
      setPacientes(rPac.data);
      setAlertas(rAlertas.data);
      if (rNotifs.ok) setNotifs(rNotifs.data || []);
      setError(null);

      // Cargar gráfica 7 días
      const base = ultimos7Dias();
      try {
        const proms = base.map(d => api.reportes.dia({ fecha: d.fecha }));
        const rs = await Promise.all(proms);
        const conData = base.map((d, i) => ({
          ...d,
          total:    rs[i]?.ok ? Number(rs[i].data?.totalFacturado || 0) : 0,
          facturas: rs[i]?.ok ? (rs[i].data?.porCategoria?.length || 0) : 0,
        }));
        setSemana(conData);
      } catch (_) { /* gráfica opcional */ }

    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
      setRefreshing(false);
      setUltimaAct(new Date());
    }
  }, [area]);

  useEffect(() => {
    cargarDatos();
    const interval = setInterval(() => cargarDatos(true), 15000);
    return () => clearInterval(interval);
  }, [cargarDatos]);

  // ── Cargar panel médico ("Mi panel") ──────────────────────
  // Independiente del refresh general: solo corre si el rol es médico
  // y el usuario tiene medico_id vinculado (ver Usuarios → vínculo con
  // catálogo de Médicos). Si no está vinculado, no se llama a la API y
  // se muestra un aviso en vez de listas vacías sin explicación.
  const cargarPanelMedico = useCallback(async () => {
    if (!esRolMedico || !usuario?.medico_id) return;
    setCargandoMedico(true);
    try {
      const [rPac, rLabs, rPresc] = await Promise.all([
        api.dashboard.medicoPacientes(usuario.medico_id),
        api.dashboard.medicoLabsPendientes(usuario.medico_id),
        api.dashboard.medicoPrescripcionesPendientes(usuario.medico_id),
      ]);
      if (rPac.ok)   setMisPacientes(rPac.data || []);
      if (rLabs.ok)  setMisLabs(rLabs.data || []);
      if (rPresc.ok) setMisPrescrip(rPresc.data || []);
    } finally {
      setCargandoMedico(false);
    }
  }, [esRolMedico, usuario?.medico_id]);

  useEffect(() => {
    cargarPanelMedico();
    const interval = setInterval(cargarPanelMedico, 30000);
    return () => clearInterval(interval);
  }, [cargarPanelMedico]);

  // ── Cargar panel de enfermería ─────────────────────────────
  // Reutiliza indicacionesEnf.pendientes() (ya usado en el módulo
  // Enfermería → tab "Indicaciones pendientes"). No requiere handler
  // nuevo: el listado completo ya trae estado, y aquí solo se cuenta
  // por categoría para los KPIs del Dashboard.
  const cargarPanelEnfermeria = useCallback(async () => {
    if (!esRolEnfermeria) return;
    setCargandoIndicEnf(true);
    try {
      const r = await api.indicacionesEnf.pendientes();
      if (r.ok) setIndicEnf(r.data || []);
    } finally {
      setCargandoIndicEnf(false);
    }
  }, [esRolEnfermeria]);

  useEffect(() => {
    cargarPanelEnfermeria();
    const interval = setInterval(cargarPanelEnfermeria, 30000);
    return () => clearInterval(interval);
  }, [cargarPanelEnfermeria]);

  // ── Cargar panel de laboratorio ────────────────────────────
  // Reutiliza ordenesLab.pendientes() (ya usado en el módulo Laboratorio
  // → tab "Pendientes") y agrega lab.resultadosHoy() (nuevo, solo cuenta).
  const cargarPanelLab = useCallback(async () => {
    if (!esRolLaboratorio) return;
    setCargandoLab(true);
    try {
      const [rOrd, rHoy] = await Promise.all([
        api.ordenesLab.pendientes(),
        api.lab.resultadosHoy(), // antes: window.altimo.laboratorio.resultadosHoy()
      ]);
      if (rOrd.ok) setOrdenesLabPend(rOrd.data || []);
      if (rHoy.ok) setResultadosHoyLab(rHoy.data || 0);
    } finally {
      setCargandoLab(false);
    }
  }, [esRolLaboratorio]);

  useEffect(() => {
    cargarPanelLab();
    const interval = setInterval(cargarPanelLab, 30000);
    return () => clearInterval(interval);
  }, [cargarPanelLab]);

  // ── Cargar panel de farmacia ────────────────────────────────
  // Reutiliza inventario.listar() (mismo cálculo de stock crítico que ya
  // hace el módulo Farmacia → header de KPIs) y los pendientes de
  // prescripciones/devoluciones (ya usados en el tab "Pendientes").
  const cargarPanelFarmacia = useCallback(async () => {
    if (!esRolFarmacia) return;
    setCargandoFarm(true);
    try {
      // NOTA: prescripciones/devoluciones aún no tienen módulo en el
      // servidor (pendiente de migrar). Mientras tanto, solo se carga
      // el inventario real; las otras dos listas quedan vacías sin
      // romper el resto del panel.
      const rInv = await api.inventario.listar();
      if (rInv.ok) setInventarioFarm(rInv.data || []);
      setPrescripPendFarm([]);
      setDevolucPendFarm([]);
    } finally {
      setCargandoFarm(false);
    }
  }, [esRolFarmacia]);

  useEffect(() => {
    cargarPanelFarmacia();
    const interval = setInterval(cargarPanelFarmacia, 30000);
    return () => clearInterval(interval);
  }, [cargarPanelFarmacia]);

  // ── Marcar notificación leída ─────────────────────────────
  const marcarLeida = async (id) => {
    await api.notificaciones.marcarLeida({ id });
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, leida: 1 } : n));
  };

  const marcarTodasLeidas = async () => {
    await api.notificaciones.marcarTodasLeidas({ area, rol: usuario?.rol });
    setNotifs(prev => prev.map(n => ({ ...n, leida: 1 })));
  };

  // ── Scroll a tabla ────────────────────────────────────────
  const irATabla = () => {
    tablaRef.current?.scrollIntoView({ behavior: 'smooth' });
    setKpiActivo('cuentas');
  };

  if (cargando) return (
    <div style={{ ...S.pagina, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>🏥</div>
        <div style={{ color: COLOR.azul, fontSize: '15px', fontWeight: '600' }}>Cargando ALTIMO Dashboard...</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={S.pagina}>
      <div style={{ padding: '16px', borderRadius: '10px', background: COLOR.rojoClaro, color: '#721c24', fontSize: '14px', marginBottom: '12px', border: `1px solid #f5c6cb` }}>
        ⚠ Error al cargar datos: {error}
      </div>
      <button style={S.btnPrimario} onClick={() => cargarDatos()}>Reintentar</button>
    </div>
  );

  // Alertas del sistema agrupan 2 dominios distintos: inventario/farmacia
  // (stock bajo, por vencer) y facturación/caja (cuentas sin facturar).
  // Cada rol solo debe ver lo que le compete — igual que PERMISOS en App.jsx.
  const verAlertasInventario  = ['admin', 'farmacia'].includes(usuario?.rol);
  const verAlertasFacturacion = ['admin', 'caja'].includes(usuario?.rol);

  const totalAlertas =
    (verAlertasInventario  ? (alertas?.stockBajo?.length || 0) + (alertas?.porVencer?.length || 0) : 0) +
    (verAlertasFacturacion ? (alertas?.cuentasSinFacturar?.length || 0) : 0);

  const notifsNoLeidas = notifs.filter(n => !n.leida).length;
  const totalFacturadoSemana = semana.reduce((s, d) => s + (d.total || 0), 0);

  // Panel de enfermería: conteos derivados del listado de indicaciones ya
  // cargado (mismo dato que usa el módulo Enfermería → "Indicaciones
  // pendientes"), sin necesidad de un endpoint nuevo.
  const indicVencidas   = indicEnf.filter(i => i.estado === 'Vencida').length;
  const indicProximas    = indicEnf.filter(i => i.estado === 'Pendiente' || i.estado === 'Notificada').length;

  // Panel de laboratorio: conteos derivados del listado de órdenes ya
  // cargado (mismo dato que usa el módulo Laboratorio → "Pendientes").
  const ordenesPorTomar = ordenesLabPend.filter(o => o.estado === 'Pendiente').length;
  const ordenesEnProceso = ordenesLabPend.filter(o => o.estado === 'En_Proceso').length;

  // Panel de farmacia: mismo cálculo de "stock crítico" que ya usa
  // Farmacia.jsx en su propio header de KPIs (sin stock + stock bajo).
  const stockCriticoFarm = inventarioFarm.filter(p =>
    p.stock <= 0 || (p.stock > 0 && p.stock <= p.stock_minimo)
  ).length;

  // Pacientes filtrados
  const pacientesFiltrados = pacientes.filter(p => {
    if (filtroEstado === 'Todos') return true;
    return p.estado === filtroEstado;
  });

  return (
    <div style={S.pagina}>
      {/* ── CSS animación pulse ── */}
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1}50%{opacity:.4} }
        .altimo-tr:hover td { background: #f8fafd !important; }
      `}</style>

      {/* ── Header ── */}
      <div style={S.header}>
        <div>
          <div style={S.headerTitulo}>🏥 ALTIMO Medical Center</div>
          <div style={S.headerSub}>
            <span style={S.refreshDot(refreshing)} />
            {ultimaAct
              ? `Actualizado: ${ultimaAct.toLocaleTimeString('es-SV')} · Refresca automáticamente cada 60s`
              : 'Cargando...'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {notifsNoLeidas > 0 && (
            <span style={{ background: COLOR.rojo, color: '#fff', borderRadius: '99px', padding: '3px 10px', fontSize: '11px', fontWeight: '700' }}>
              🔔 {notifsNoLeidas} nueva{notifsNoLeidas !== 1 ? 's' : ''}
            </span>
          )}
          <button style={S.btnSecundario} onClick={() => cargarDatos()} disabled={refreshing}>
            {refreshing ? '⏳ Actualizando...' : '🔄 Actualizar'}
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          MI PANEL — solo rol médico / jefe médico de turno
      ══════════════════════════════════════════════════════ */}
      {esRolMedico && (
        <div style={{ ...S.card, border: `2px solid ${COLOR.azul}`, background:'linear-gradient(180deg,#f5f8fd 0%,#fff 60px)' }}>
          <div style={{ ...S.cardTitulo, cursor:'pointer' }} onClick={() => setMiPanelOpen(o => !o)}>
            <span>👨‍⚕️ Mi panel{usuario?.nombre ? ` — ${usuario.nombre}` : ''}</span>
            <span style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              {onIrAModulo && (
                <span title="Ir al módulo Clínico / Médico"
                  onClick={e => { e.stopPropagation(); onIrAModulo('clinica'); }}
                  style={{ fontSize:'12px', color: COLOR.azul, cursor:'pointer' }}>↗</span>
              )}
              <span style={{ fontSize:'11px', color: COLOR.gris }}>{miPanelOpen ? '▲' : '▼'}</span>
            </span>
          </div>

          {!usuario?.medico_id ? (
            <div style={{ padding:'14px 16px', borderRadius:'8px', background:'#fff3cd', border:'1px solid #ffc107', color:'#856404', fontSize:'13px' }}>
              ⚠ Tu usuario no está vinculado a un registro del catálogo de Médicos, así que no podemos mostrarte
              tus pacientes, laboratorios ni prescripciones automáticamente. Pide a un administrador que lo vincule
              desde <strong>Usuarios → Editar → Médico vinculado</strong>.
            </div>
          ) : miPanelOpen && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'14px' }}>

              {/* Mis pacientes activos */}
              <div>
                <div style={{ fontSize:'11px', fontWeight:'700', color: COLOR.azul, marginBottom:'8px', display:'flex', justifyContent:'space-between' }}>
                  <span>🛏 Mis pacientes activos</span>
                  <span style={{ ...S.pill(COLOR.azulClaro, COLOR.azul) }}>{misPacientes.length}</span>
                </div>
                {cargandoMedico ? (
                  <div style={{ fontSize:'12px', color:'#adb5bd', padding:'10px' }}>Cargando...</div>
                ) : misPacientes.length === 0 ? (
                  <div style={{ fontSize:'12px', color:'#adb5bd', padding:'10px' }}>Sin pacientes activos asignados.</div>
                ) : (
                  <div style={{ maxHeight:'220px', overflowY:'auto' }}>
                    {misPacientes.map(p => (
                      <div key={p.admision_id}
                        onClick={() => onVerCuenta && onVerCuenta(p.admision_id)}
                        style={{ padding:'8px 10px', borderRadius:'7px', marginBottom:'5px', background:'#f8fafd', border:`1px solid ${COLOR.borde}`, cursor:'pointer', fontSize:'12px' }}>
                        <div style={{ fontWeight:'700', color: COLOR.azul }}>{p.paciente}</div>
                        <div style={{ color: COLOR.gris, marginTop:'2px' }}>
                          {p.habitacion_num ? `Hab. ${p.habitacion_num} · ` : ''}{p.dias}d de estancia
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Labs pendientes de revisar */}
              <div>
                <div style={{ fontSize:'11px', fontWeight:'700', color: COLOR.azul, marginBottom:'8px', display:'flex', justifyContent:'space-between' }}>
                  <span>🧪 Labs pendientes de revisar</span>
                  <span style={{ ...S.pill(misLabs.length > 0 ? COLOR.amarilloClaro : COLOR.azulClaro, misLabs.length > 0 ? '#856404' : COLOR.azul) }}>{misLabs.length}</span>
                </div>
                {cargandoMedico ? (
                  <div style={{ fontSize:'12px', color:'#adb5bd', padding:'10px' }}>Cargando...</div>
                ) : misLabs.length === 0 ? (
                  <div style={{ fontSize:'12px', color:'#adb5bd', padding:'10px' }}>✓ Sin resultados pendientes de revisión.</div>
                ) : (
                  <div style={{ maxHeight:'220px', overflowY:'auto' }}>
                    {misLabs.map(l => (
                      <div key={l.id}
                        onClick={() => {
                          if (l.paciente_id && onIrAExpedienteLab) onIrAExpedienteLab(l.paciente_id, l.admision_id);
                          else onVerCuenta && onVerCuenta(l.admision_id);
                        }}
                        style={{ padding:'8px 10px', borderRadius:'7px', marginBottom:'5px', background:'#fff3cd', border:'1px solid #ffe082', cursor:'pointer', fontSize:'12px' }}>
                        <div style={{ fontWeight:'700', color:'#856404' }}>{l.paciente}</div>
                        <div style={{ color:'#856404', marginTop:'2px' }}>{l.tipo_examen}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Prescripciones sin dispensar */}
              <div>
                <div style={{ fontSize:'11px', fontWeight:'700', color: COLOR.azul, marginBottom:'8px', display:'flex', justifyContent:'space-between' }}>
                  <span>💊 Prescripciones sin dispensar</span>
                  <span style={{ ...S.pill(COLOR.azulClaro, COLOR.azul) }}>{misPrescrip.length}</span>
                </div>
                {cargandoMedico ? (
                  <div style={{ fontSize:'12px', color:'#adb5bd', padding:'10px' }}>Cargando...</div>
                ) : misPrescrip.length === 0 ? (
                  <div style={{ fontSize:'12px', color:'#adb5bd', padding:'10px' }}>✓ Todas tus prescripciones han sido dispensadas.</div>
                ) : (
                  <div style={{ maxHeight:'220px', overflowY:'auto' }}>
                    {misPrescrip.map(pr => (
                      <div key={pr.id}
                        onClick={() => onVerCuenta && onVerCuenta(pr.admision_id)}
                        style={{ padding:'8px 10px', borderRadius:'7px', marginBottom:'5px', background:'#f8fafd', border:`1px solid ${COLOR.borde}`, cursor:'pointer', fontSize:'12px' }}>
                        <div style={{ fontWeight:'700', color: COLOR.azul }}>{pr.paciente}</div>
                        <div style={{ color: COLOR.gris, marginTop:'2px' }}>{pr.descripcion}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          MI PANEL — enfermería / jefa de enfermería / supervisora
      ══════════════════════════════════════════════════════ */}
      {esRolEnfermeria && (
        <div style={{ ...S.card, border: `2px solid ${COLOR.azul}`, background:'linear-gradient(180deg,#f5f8fd 0%,#fff 60px)' }}>
          <div style={{ ...S.cardTitulo, cursor:'pointer' }} onClick={() => setEnfPanelOpen(o => !o)}>
            <span>🩺 Mi panel{usuario?.nombre ? ` — ${usuario.nombre}` : ''}</span>
            <span style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              {onIrAModulo && (
                <span title="Ir al módulo Enfermería"
                  onClick={e => { e.stopPropagation(); onIrAModulo('enfermeria'); }}
                  style={{ fontSize:'12px', color: COLOR.azul, cursor:'pointer' }}>↗</span>
              )}
              <span style={{ fontSize:'11px', color: COLOR.gris }}>{enfPanelOpen ? '▲' : '▼'}</span>
            </span>
          </div>

          {enfPanelOpen && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'14px' }}>
            <div style={{ background:'#f8fafd', border:`1px solid ${COLOR.borde}`, borderRadius:'10px', padding:'14px 16px', display:'flex', alignItems:'center', gap:'12px', cursor:'pointer' }}
              onClick={() => onIrAModulo && onIrAModulo('clinica')}>
              <div style={{ fontSize:'26px' }}>🛏</div>
              <div>
                <div style={{ fontSize:'24px', fontWeight:'800', color: COLOR.azul, lineHeight:1 }}>
                  {cargando ? '—' : pacientes.length}
                </div>
                <div style={{ fontSize:'10px', color: COLOR.gris, fontWeight:'700', textTransform:'uppercase', letterSpacing:'.04em', marginTop:'2px' }}>
                  Pacientes hospitalizados activos
                </div>
              </div>
            </div>

            <div style={{ background: indicVencidas > 0 ? '#f8d7da' : '#f8fafd', border:`1px solid ${indicVencidas > 0 ? '#f5c6cb' : COLOR.borde}`, borderRadius:'10px', padding:'14px 16px', display:'flex', alignItems:'center', gap:'12px', cursor:'pointer' }}
              onClick={() => onIrAModulo && onIrAModulo('enfermeria', { filtro: 'Vencida' })}>
              <div style={{ fontSize:'26px' }}>⚠️</div>
              <div>
                <div style={{ fontSize:'24px', fontWeight:'800', color: indicVencidas > 0 ? COLOR.rojo : COLOR.azul, lineHeight:1 }}>
                  {cargandoIndicEnf ? '—' : indicVencidas}
                </div>
                <div style={{ fontSize:'10px', color: indicVencidas > 0 ? '#721c24' : COLOR.gris, fontWeight:'700', textTransform:'uppercase', letterSpacing:'.04em', marginTop:'2px' }}>
                  Indicaciones vencidas
                </div>
              </div>
            </div>

            <div style={{ background:'#f8fafd', border:`1px solid ${COLOR.borde}`, borderRadius:'10px', padding:'14px 16px', display:'flex', alignItems:'center', gap:'12px', cursor:'pointer' }}
              onClick={() => onIrAModulo && onIrAModulo('enfermeria', { filtro: 'Proximas' })}>
              <div style={{ fontSize:'26px' }}>🔔</div>
              <div>
                <div style={{ fontSize:'24px', fontWeight:'800', color: COLOR.azul, lineHeight:1 }}>
                  {cargandoIndicEnf ? '—' : indicProximas}
                </div>
                <div style={{ fontSize:'10px', color: COLOR.gris, fontWeight:'700', textTransform:'uppercase', letterSpacing:'.04em', marginTop:'2px' }}>
                  Indicaciones pendientes / próximas
                </div>
              </div>
            </div>
          </div>
          )}

          <div style={{ fontSize:'11px', color: COLOR.gris, marginTop:'10px', textAlign:'right' }}>
            Detalle completo en el módulo Enfermería → Indicaciones pendientes
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          MI PANEL — laboratorio
      ══════════════════════════════════════════════════════ */}
      {esRolLaboratorio && (
        <div style={{ ...S.card, border: `2px solid ${COLOR.azul}`, background:'linear-gradient(180deg,#f5f8fd 0%,#fff 60px)' }}>
          <div style={{ ...S.cardTitulo, cursor:'pointer' }} onClick={() => setLabPanelOpen(o => !o)}>
            <span>🧪 Mi panel{usuario?.nombre ? ` — ${usuario.nombre}` : ''}</span>
            <span style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              {onIrAModulo && (
                <span title="Ir al módulo Laboratorio"
                  onClick={e => { e.stopPropagation(); onIrAModulo('laboratorio'); }}
                  style={{ fontSize:'12px', color: COLOR.azul, cursor:'pointer' }}>↗</span>
              )}
              <span style={{ fontSize:'11px', color: COLOR.gris }}>{labPanelOpen ? '▲' : '▼'}</span>
            </span>
          </div>

          {labPanelOpen && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'14px' }}>
            <div style={{ background: ordenesPorTomar > 0 ? '#fff3cd' : '#f8fafd', border:`1px solid ${ordenesPorTomar > 0 ? '#ffe082' : COLOR.borde}`, borderRadius:'10px', padding:'14px 16px', display:'flex', alignItems:'center', gap:'12px', cursor:'pointer' }}
              onClick={() => onIrAModulo && onIrAModulo('laboratorio', { filtro: 'Pendiente' })}>
              <div style={{ fontSize:'26px' }}>🔔</div>
              <div>
                <div style={{ fontSize:'24px', fontWeight:'800', color: ordenesPorTomar > 0 ? '#856404' : COLOR.azul, lineHeight:1 }}>
                  {cargandoLab ? '—' : ordenesPorTomar}
                </div>
                <div style={{ fontSize:'10px', color: ordenesPorTomar > 0 ? '#856404' : COLOR.gris, fontWeight:'700', textTransform:'uppercase', letterSpacing:'.04em', marginTop:'2px' }}>
                  Órdenes pendientes de tomar
                </div>
              </div>
            </div>

            <div style={{ background:'#f8fafd', border:`1px solid ${COLOR.borde}`, borderRadius:'10px', padding:'14px 16px', display:'flex', alignItems:'center', gap:'12px', cursor:'pointer' }}
              onClick={() => onIrAModulo && onIrAModulo('laboratorio', { filtro: 'En_Proceso' })}>
              <div style={{ fontSize:'26px' }}>🧪</div>
              <div>
                <div style={{ fontSize:'24px', fontWeight:'800', color: COLOR.azul, lineHeight:1 }}>
                  {cargandoLab ? '—' : ordenesEnProceso}
                </div>
                <div style={{ fontSize:'10px', color: COLOR.gris, fontWeight:'700', textTransform:'uppercase', letterSpacing:'.04em', marginTop:'2px' }}>
                  Exámenes en proceso
                </div>
              </div>
            </div>

            <div style={{ background:'#f8fafd', border:`1px solid ${COLOR.borde}`, borderRadius:'10px', padding:'14px 16px', display:'flex', alignItems:'center', gap:'12px' }}>
              <div style={{ fontSize:'26px' }}>✅</div>
              <div>
                <div style={{ fontSize:'24px', fontWeight:'800', color: COLOR.verde, lineHeight:1 }}>
                  {cargandoLab ? '—' : resultadosHoyLab}
                </div>
                <div style={{ fontSize:'10px', color: COLOR.gris, fontWeight:'700', textTransform:'uppercase', letterSpacing:'.04em', marginTop:'2px' }}>
                  Resultados subidos hoy
                </div>
              </div>
            </div>
          </div>
          )}

          <div style={{ fontSize:'11px', color: COLOR.gris, marginTop:'10px', textAlign:'right' }}>
            Detalle completo en el módulo Laboratorio → Pendientes
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          MI PANEL — farmacia
      ══════════════════════════════════════════════════════ */}
      {esRolFarmacia && (
        <div style={{ ...S.card, border: `2px solid ${COLOR.azul}`, background:'linear-gradient(180deg,#f5f8fd 0%,#fff 60px)' }}>
          <div style={{ ...S.cardTitulo, cursor:'pointer' }} onClick={() => setFarmPanelOpen(o => !o)}>
            <span>💊 Mi panel{usuario?.nombre ? ` — ${usuario.nombre}` : ''}</span>
            <span style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              {onIrAModulo && (
                <span title="Ir al módulo Farmacia"
                  onClick={e => { e.stopPropagation(); onIrAModulo('farmacia'); }}
                  style={{ fontSize:'12px', color: COLOR.azul, cursor:'pointer' }}>↗</span>
              )}
              <span style={{ fontSize:'11px', color: COLOR.gris }}>{farmPanelOpen ? '▲' : '▼'}</span>
            </span>
          </div>

          {farmPanelOpen && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'14px' }}>
            <div style={{ background: stockCriticoFarm > 0 ? '#f8d7da' : '#f8fafd', border:`1px solid ${stockCriticoFarm > 0 ? '#f5c6cb' : COLOR.borde}`, borderRadius:'10px', padding:'14px 16px', display:'flex', alignItems:'center', gap:'12px', cursor:'pointer' }}
              onClick={() => onIrAModulo && onIrAModulo('farmacia', { filtro: { vista: 'catalogo', alerta: 'Critico' } })}>
              <div style={{ fontSize:'26px' }}>📦</div>
              <div>
                <div style={{ fontSize:'24px', fontWeight:'800', color: stockCriticoFarm > 0 ? COLOR.rojo : COLOR.azul, lineHeight:1 }}>
                  {cargandoFarm ? '—' : stockCriticoFarm}
                </div>
                <div style={{ fontSize:'10px', color: stockCriticoFarm > 0 ? '#721c24' : COLOR.gris, fontWeight:'700', textTransform:'uppercase', letterSpacing:'.04em', marginTop:'2px' }}>
                  Stock crítico (sin stock + bajo)
                </div>
              </div>
            </div>

            <div style={{ background: prescripPendFarm.length > 0 ? '#fff3cd' : '#f8fafd', border:`1px solid ${prescripPendFarm.length > 0 ? '#ffe082' : COLOR.borde}`, borderRadius:'10px', padding:'14px 16px', display:'flex', alignItems:'center', gap:'12px', cursor:'pointer' }}
              onClick={() => onIrAModulo && onIrAModulo('farmacia', { filtro: { vista: 'pendientes', sub: 'prescripciones' } })}>
              <div style={{ fontSize:'26px' }}>💊</div>
              <div>
                <div style={{ fontSize:'24px', fontWeight:'800', color: prescripPendFarm.length > 0 ? '#856404' : COLOR.azul, lineHeight:1 }}>
                  {cargandoFarm ? '—' : prescripPendFarm.length}
                </div>
                <div style={{ fontSize:'10px', color: prescripPendFarm.length > 0 ? '#856404' : COLOR.gris, fontWeight:'700', textTransform:'uppercase', letterSpacing:'.04em', marginTop:'2px' }}>
                  Prescripciones sin dispensar
                </div>
              </div>
            </div>

            <div style={{ background:'#f8fafd', border:`1px solid ${COLOR.borde}`, borderRadius:'10px', padding:'14px 16px', display:'flex', alignItems:'center', gap:'12px', cursor:'pointer' }}
              onClick={() => onIrAModulo && onIrAModulo('farmacia', { filtro: { vista: 'pendientes', sub: 'devoluciones' } })}>
              <div style={{ fontSize:'26px' }}>↩</div>
              <div>
                <div style={{ fontSize:'24px', fontWeight:'800', color: COLOR.azul, lineHeight:1 }}>
                  {cargandoFarm ? '—' : devolucPendFarm.length}
                </div>
                <div style={{ fontSize:'10px', color: COLOR.gris, fontWeight:'700', textTransform:'uppercase', letterSpacing:'.04em', marginTop:'2px' }}>
                  Devoluciones sin confirmar
                </div>
              </div>
            </div>
          </div>
          )}

          <div style={{ fontSize:'11px', color: COLOR.gris, marginTop:'10px', textAlign:'right' }}>
            Detalle completo en el módulo Farmacia
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          KPIs — ocultos para roles médico/jefe_medico,
          enfermería/jefa_enfermeria/supervisora_turno,
          laboratorio y farmacia: son operativos/financieros
          (facturación, habitaciones) y no aportan a esos flujos.
          "Mi panel" arriba ya cubre lo relevante para esos roles.
      ══════════════════════════════════════════════════════ */}
      {!esRolMedico && !esRolEnfermeria && !esRolLaboratorio && !esRolFarmacia && (
      <div style={S.kpiGrid}>
        <KpiCard
          label="Pacientes hoy"
          valor={kpis?.pacientesHoy ?? 0}
          sub={`${kpis?.admisionesActivas ?? 0} hospitalizados activos`}
          color={COLOR.azul}
          icono="👥"
          onClick={() => { setKpiActivo(k => k === 'pacientes' ? null : 'pacientes'); irATabla(); }}
          activo={kpiActivo === 'pacientes'}
        />
        <KpiCard
          label="Facturado hoy"
          valor={`$${(kpis?.facturadoHoy ?? 0).toLocaleString('es-SV', { minimumFractionDigits: 2 })}`}
          sub={`Semana: $${totalFacturadoSemana.toLocaleString('es-SV', { minimumFractionDigits: 2 })}`}
          color={COLOR.verde}
          icono="💰"
          onClick={() => setKpiActivo(k => k === 'revenue' ? null : 'revenue')}
          activo={kpiActivo === 'revenue'}
        />
        <KpiCard
          label="Cuentas abiertas"
          valor={kpis?.admisionesActivas ?? 0}
          sub="Sin facturar"
          color={(kpis?.admisionesActivas ?? 0) > 5 ? COLOR.rojo : COLOR.azul}
          icono="📋"
          onClick={() => { setKpiActivo(k => k === 'cuentas' ? null : 'cuentas'); irATabla(); }}
          activo={kpiActivo === 'cuentas'}
        />
        <KpiCard
          label="Habitaciones libres"
          valor={`${kpis?.habitacionesLibres ?? 0} / ${kpis?.habitacionesTotal ?? 0}`}
          sub={`${kpis?.habitacionesTotal - kpis?.habitacionesLibres ?? 0} ocupadas`}
          color={(kpis?.habitacionesLibres ?? 0) === 0 ? COLOR.rojo : COLOR.azul}
          icono="🛏"
          onClick={() => setKpiActivo(k => k === 'hab' ? null : 'hab')}
          activo={kpiActivo === 'hab'}
        />
      </div>
      )}

      {/* ── Gráfica 7 días (expandible) — depende de kpiActivo,
          que solo se activa desde los KPIs ocultos arriba, así que
          se oculta junto con ellos para el rol médico. ── */}
      {!esRolMedico && !esRolEnfermeria && !esRolLaboratorio && !esRolFarmacia && kpiActivo === 'revenue' && (
        <div style={S.card}>
          <div style={S.cardTitulo}>
            <span>📈 Ingresos — últimos 7 días</span>
            <span style={{ fontSize: '13px', color: COLOR.gris, fontWeight: '400' }}>
              Total: ${totalFacturadoSemana.toFixed(2)}
            </span>
          </div>
          <MiniBarChart datos={semana} onDiaClick={d => setDiaActivo(a => a === d.fecha ? null : d.fecha)} diaActivo={diaActivo} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '4px', marginTop: '6px' }}>
            {semana.map(d => (
              <div key={d.fecha} style={{ textAlign: 'center', fontSize: '10px', color: COLOR.gris }}>
                ${Number(d.total).toFixed(0)}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={S.fila3}>
        {/* ══ Tabla de pacientes activos ══ */}
        <div style={S.card} ref={tablaRef}>
          <div style={S.cardTitulo}>
            <span>👥 Pacientes activos</span>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {/* Filtro rápido */}
              {['Todos','Activa','Alta'].map(f => (
                <button key={f}
                  onClick={() => setFiltroEstado(f)}
                  style={{
                    padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                    cursor: 'pointer', border: 'none',
                    background: filtroEstado === f ? COLOR.azul : COLOR.azulClaro,
                    color: filtroEstado === f ? '#fff' : COLOR.azul,
                  }}>
                  {f} {f === 'Todos' ? `(${pacientes.length})` : `(${pacientes.filter(p=>p.estado===f).length})`}
                </button>
              ))}
              <button style={S.btnSecundario} onClick={() => cargarDatos(true)}>⟳</button>
            </div>
          </div>

          {pacientesFiltrados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '28px', color: '#adb5bd', fontSize: '13px' }}>
              No hay pacientes activos en este momento.
            </div>
          ) : (
            <table style={S.tabla}>
              <thead>
                <tr>
                  {(verMontos
                    ? ['Paciente','Área','Días','Acumulado','Estado','Acción']
                    : ['Paciente','Área','Días','Estado','Acción']
                  ).map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pacientesFiltrados.map(p => (
                  <Fragment key={p.id}>
                    <tr className="altimo-tr"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setRowAbierta(r => r === p.id ? null : p.id)}>
                      <td style={S.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '16px' }}>
                            {rowAbierta === p.id ? '▼' : '▶'}
                          </span>
                          <div>
                            <div style={{ fontWeight: '700', color: COLOR.azul }}>{p.paciente}</div>
                            <div style={{ fontSize: '10px', color: COLOR.gris }}>{p.tipo_ingreso}</div>
                          </div>
                        </div>
                      </td>
                      <td style={S.td}>
                        {p.habitacion_num
                          ? <><strong>{p.habitacion_num}</strong> · {p.tipo}</>
                          : <span style={{ color: '#adb5bd' }}>—</span>}
                      </td>
                      <td style={{ ...S.td, fontWeight: '700' }}>
                        {p.dias}d
                      </td>
                      {verMontos && (
                        <td style={{ ...S.td, fontWeight: '700', color: COLOR.azul }}>
                          ${Number(p.acumulado).toLocaleString('es-SV', { minimumFractionDigits: 2 })}
                        </td>
                      )}
                      <td style={S.td}><PillEstado texto={p.estado === 'Activa' ? 'Activa' : 'Alta'} /></td>
                      <td style={S.td}>
                        <button style={S.btnPrimario}
                          onClick={e => { e.stopPropagation(); onVerCuenta && onVerCuenta(p.id); }}>
                          Ver cuenta
                        </button>
                      </td>
                    </tr>
                    {/* Fila de detalle expandible */}
                    {rowAbierta === p.id && (
                      <tr>
                        <td colSpan={verMontos ? 6 : 5} style={{ padding: 0 }}>
                          <div style={S.rowDetalle}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '10px' }}>
                              <div><span style={{ color: COLOR.gris }}>DUI:</span> <strong>{p.dui || '—'}</strong></div>
                              <div><span style={{ color: COLOR.gris }}>Teléfono:</span> <strong>{p.telefono || '—'}</strong></div>
                              <div><span style={{ color: COLOR.gris }}>Médico:</span> <strong>{p.medico_nombre || '—'}</strong></div>
                              <div><span style={{ color: COLOR.gris }}>Ingreso:</span> <strong>{p.fecha_ingreso ? new Date(p.fecha_ingreso).toLocaleDateString('es-SV') : '—'}</strong></div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ══ Panel derecho: Notificaciones + Alertas ══ */}
        <div>
          {/* ── Notificaciones del área ── */}
          <div style={S.card}>
            <div style={{ ...S.cardTitulo, cursor: 'pointer' }} onClick={() => setNotifsOpen(o => !o)}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                🔔 Notificaciones
                {notifsNoLeidas > 0 && (
                  <span style={{ background: COLOR.rojo, color: '#fff', borderRadius: '99px', padding: '1px 7px', fontSize: '10px', fontWeight: '700' }}>
                    {notifsNoLeidas}
                  </span>
                )}
              </span>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                {notifsNoLeidas > 0 && (
                  <button style={{ ...S.btnSecundario, fontSize: '10px', padding: '3px 8px' }}
                    onClick={e => { e.stopPropagation(); marcarTodasLeidas(); }}>
                    Marcar todas
                  </button>
                )}
                <span style={{ fontSize: '14px', color: COLOR.gris }}>{notifsOpen ? '▲' : '▼'}</span>
              </div>
            </div>

            {notifsOpen && (
              notifs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#adb5bd', fontSize: '12px' }}>
                  ✓ Sin notificaciones pendientes
                </div>
              ) : (
                <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                  {notifs.slice(0, 20).map(n => (
                    <div key={n.id}
                      style={S.notifCard(n.leida)}
                      onClick={() => !n.leida && marcarLeida(n.id)}
                      onMouseEnter={e => { if (!n.leida) e.currentTarget.style.background = '#fff3cd'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = n.leida ? '#f8fafd' : '#fff'; }}>
                      <div style={S.notifPunto(n.leida)} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
                          <div style={S.notifTitulo(n.leida)}>{n.titulo}</div>
                          {n.prioridad && (
                            <span style={{
                              fontSize:'9px', fontWeight:'800', textTransform:'uppercase', borderRadius:'99px', padding:'2px 6px',
                              background: n.prioridad === 'alta' ? '#f8d7da' : n.prioridad === 'media' ? '#fff3cd' : '#e2e3e5',
                              color: n.prioridad === 'alta' ? '#721c24' : n.prioridad === 'media' ? '#856404' : '#383d41',
                            }}>{n.prioridad}</span>
                          )}
                        </div>
                        {n.cuerpo && <div style={{ fontSize: '11px', color: COLOR.gris, marginTop: '2px' }}>{n.cuerpo}</div>}
                        {n.paciente && <div style={{ fontSize: '10px', color: COLOR.verde, fontWeight: '600', marginTop: '2px' }}>👤 {n.paciente}</div>}
                        <div style={S.notifFecha}>
                          {n.area !== 'Todas' && <span style={{ fontWeight: '700', marginRight: '4px' }}>[{n.area}]</span>}
                          {n.fecha ? new Date(n.fecha).toLocaleString('es-SV') : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>

          {/* ── Alertas del sistema ── */}
          {totalAlertas > 0 && (
            <div style={S.card}>
              <div style={{ ...S.cardTitulo, cursor: 'pointer' }} onClick={() => setAlertasOpen(o => !o)}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ background: COLOR.rojo, color: '#fff', borderRadius: '99px', padding: '1px 7px', fontSize: '10px', fontWeight: '700' }}>
                    {totalAlertas}
                  </span>
                  Alertas del sistema
                </span>
                <span style={{ fontSize: '14px', color: COLOR.gris }}>{alertasOpen ? '▲' : '▼'}</span>
              </div>

              {alertasOpen && (
                <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                  {verAlertasInventario && alertas?.stockBajo?.length > 0 && (
                    <>
                      <div style={{ fontSize: '10px', fontWeight: '700', color: '#721c24', background: '#f8d7da', padding: '3px 8px', borderRadius: '4px', display: 'inline-block', marginBottom: '6px', textTransform: 'uppercase' }}>
                        📦 Stock bajo ({alertas.stockBajo.length})
                      </div>
                      {alertas.stockBajo.map((a, i) => (
                        <div key={i} style={{ padding: '7px 10px', borderRadius: '7px', marginBottom: '5px', fontSize: '12px', background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb' }}>
                          <strong>{a.nombre}</strong> — {a.stock} {a.unidad} (mín: {a.stock_minimo})
                        </div>
                      ))}
                    </>
                  )}
                  {verAlertasInventario && alertas?.porVencer?.length > 0 && (
                    <>
                      <div style={{ fontSize: '10px', fontWeight: '700', color: '#856404', background: '#fff3cd', padding: '3px 8px', borderRadius: '4px', display: 'inline-block', marginBottom: '6px', textTransform: 'uppercase', marginTop: '8px' }}>
                        ⏰ Por vencer ({alertas.porVencer.length})
                      </div>
                      {alertas.porVencer.map((a, i) => (
                        <div key={i} style={{ padding: '7px 10px', borderRadius: '7px', marginBottom: '5px', fontSize: '12px', background: '#fff3cd', color: '#856404', border: '1px solid #ffc107' }}>
                          <strong>{a.nombre}</strong> — vence: {a.fecha_vencimiento}
                        </div>
                      ))}
                    </>
                  )}
                  {verAlertasFacturacion && alertas?.cuentasSinFacturar?.length > 0 && (
                    <>
                      <div style={{ fontSize: '10px', fontWeight: '700', color: '#856404', background: '#fff3cd', padding: '3px 8px', borderRadius: '4px', display: 'inline-block', marginBottom: '6px', textTransform: 'uppercase', marginTop: '8px' }}>
                        🧾 Sin facturar ({alertas.cuentasSinFacturar.length})
                      </div>
                      {alertas.cuentasSinFacturar.map((a, i) => (
                        <div key={i} style={{ padding: '7px 10px', borderRadius: '7px', marginBottom: '5px', fontSize: '12px', background: '#fff3cd', color: '#856404', border: '1px solid #ffc107', cursor: 'pointer' }}
                          onClick={() => onVerCuenta && onVerCuenta(a.admision_id)}>
                          <strong>{a.paciente}</strong> — {a.dias} días sin facturar →
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {totalAlertas === 0 && (
            <div style={{ ...S.card, background: '#d4edda', border: `1px solid #c3e6cb` }}>
              <div style={{ color: '#155724', fontSize: '13px', fontWeight: '600', textAlign: 'center' }}>
                ✓ Sin alertas activas del sistema
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
